import React, { useMemo, useRef, useState } from "react";
import type { TableColumnsType, TableProps } from "antd";
import {
  Button,
  Flex,
  message,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  CopyOutlined,
  DeleteFilled,
  EyeFilled,
  SearchOutlined,
} from "@ant-design/icons";
import type { InputRef } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import { Input } from "antd";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  dealerApproval,
  getDealers,
  updateDealerStatus,
} from "../../services/dealerService";
import DealerResponse from "../../payload/response/DealerResponse";
import { useNavigate } from "react-router-dom";
import { onError } from "../../helper/helper";
import dayjs from "dayjs";
type OnChange = NonNullable<TableProps<DataType>["onChange"]>;
type Filters = Parameters<OnChange>[1];

type GetSingle<T> = T extends (infer U)[] ? U : never;
type Sorts = GetSingle<Parameters<OnChange>[2]>;

interface DataType extends DealerResponse { }
const { Title } = Typography;
const Dealer: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>("");
  const searchInput = useRef<InputRef>(null);
  const {
    data: dealers,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["dealers"],
    queryFn: getDealers,
  });

  const [filteredInfo, setFilteredInfo] = useState<Filters>({});
  const [sortedInfo, setSortedInfo] = useState<Sorts>({});
  const handleChange: OnChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter as Sorts);
  };
  const { mutate } = useMutation({
    mutationFn: (id: number) => updateDealerStatus(id),
    onSuccess: (res) => {
      message.success("Bayi durumu güncellendi");
      queryClient.invalidateQueries({
        queryKey: ["dealers"],
      });
    },
    onError(error, variables, context) {
      message.error("Bir hata oluştu");
    },
  });
  const getColumnSearchProps = (dataIndex: keyof DataType): any => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder="Ara"
          value={selectedKeys[0] as string}
          onChange={(e) => {
            const val = e.target.value;
            setSelectedKeys(val ? [val] : []);
            confirm({ closeDropdown: false });
          }}
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Ara
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Temizle
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value: string | number | boolean, record: DataType) => {
      const recordValue = (record[dataIndex] ?? "") as unknown as string;
      return recordValue
        ?.toString()
        ?.toLowerCase()
        ?.includes((value as string).toLowerCase());
    },
    onFilterDropdownOpenChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: any) => void,
    dataIndex: keyof DataType
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex as string);
  };

  const handleReset = (
    clearFilters?: () => void,
    confirm?: (param?: any) => void
  ) => {
    clearFilters?.();
    setSearchText("");
    confirm?.({ closeDropdown: false });
  };
  const handleDealerApproval = useMutation({
    mutationFn: (id: number) => dealerApproval(id),
    onSuccess: (res) => {
      message.success("Bayi onaylandı");
      queryClient.invalidateQueries({
        queryKey: ["dealers"],
      });
    },
    onError(er: any) {
      onError(er);
    },
  });
  const columns: TableColumnsType<DataType> = [
    {
      title: "Ad Soyad",
      dataIndex: "fullName",
      key: "fullName",
      ...getColumnSearchProps("fullName"),
      sorter: (a, b) => a.fullName.length - b.fullName.length,
      sortOrder: sortedInfo.columnKey === "fullName" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Firma Adı",
      dataIndex: "companyName",
      key: "companyName",
      ...getColumnSearchProps("companyName"),
      ellipsis: true,
    },
    {
      title: "E-mail",
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email"),
      ellipsis: true,
    },
    {
      title: "Telefon",
      dataIndex: "phone",
      key: "phone",
      ...getColumnSearchProps("phone"),
      ellipsis: true,
    },
    {
      title: "GLN Numarası",
      dataIndex: "glnNumber",
      key: "glnNumber",
      ...getColumnSearchProps("glnNumber"),
      ellipsis: true,
      render: (r, record) => (
        <Space key={record.id} size="middle">
          <span>{record.glnNumber}</span>
          <CopyToClipboard
            onCopy={() => {
              message.info("Gln numarası kopyalandı");
            }}
            text={record.glnNumber}
          >
            <CopyOutlined />
          </CopyToClipboard>
        </Space>
      ),
    },
    {
      title: "Onay Durum",
      dataIndex: "isApproved",
      key: "isApproved",
      render: (text, record) => {
        return (
          <Space key={record.dealerId}>
            <Switch
              disabled={record.isActive ? true : false}
              checkedChildren="Aktif"
              unCheckedChildren="Pasif"
              onChange={(value) => {
                handleDealerApproval.mutate(record.dealerId);
              }}
              checked={record.isApproved}
            />
          </Space>
        );
      },
    },
    {
      title: "Aktiflik Durum",
      dataIndex: "isActive",
      key: "isActive",
      render: (text, record) => {
        return (
          <Space key={record.dealerId}>
            <Switch
              checkedChildren="Aktif"
              unCheckedChildren="Pasif"
              onChange={(value) => {
                if (!record.isApproved) {
                  handleDealerApproval.mutate(record.dealerId);
                } else {
                  mutate(record.dealerId);
                }
              }}
              checked={record.isActive}
            />
          </Space>
        );
      },
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
    },
    {
      title: "Oluşturulma Tarihi",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text, record) => {
        const date = new Date(record.createdAt);
        return (
          <span>
            {dayjs(date).format("DD.MM.YYYY HH:mm")}
          </span>
        );
      },
    },
    {
      title: "İşlem",
      key: "action",
      render: (_, record) => (
        <Space key={record.id} size="middle">
          <Popconfirm
            placement="bottomLeft"
            title="Silmek istediğinize emin misiniz?"
            description="Bu işlem geri alınamaz ve tüm veriler silinecektir."
            onConfirm={() => { }}
            okText="Evet"
            cancelText="Hayır"
          >
            {/* <Button type="primary" danger>
              <DeleteFilled />
            </Button> */}
          </Popconfirm>
          <Button
            onClick={() => {
              navigate(`/admin/dealer/${record.id}`);
            }}
            type="primary"
          >
            <EyeFilled />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={2}>Bayiler</Title>
      </Flex>
      <Table<DataType>
        loading={isLoading || isFetching}
        columns={columns}
        dataSource={
          dealers?.list?.map((dealer) => ({ ...dealer, key: dealer.id })) || []
        }
        onChange={handleChange}
        rowClassName={(record) => (record.isDeleteRequested ? "red-row" : "")}
      />
    </>
  );
};

export default Dealer;
